<template>
  <v-container fluid>
    <v-row class="pa-4 align-center">
      <!-- Filter Options -->
      <v-col cols="12" md="4">
        <v-select
          v-model="filters.date_field"
          :items="dateFields"
          label="Date Field"
          outlined
          dense
          hide-details
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-menu
          v-model="datePickerMenu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="filters.date"
              label="Older Than"
              readonly
              v-bind="attrs"
              v-on="on"
              outlined
              dense
              hide-details
            />
          </template>
          <v-date-picker
            v-model="filters.date"
            @input="datePickerMenu = false"
          />
        </v-menu>
      </v-col>

      <!-- Spacer and Action Buttons -->
      <v-spacer></v-spacer>
      <v-btn
        color="blue"
        icon
        class="mr-2 white--text"
        @click="downloadSelected"
        :disabled="!selected.length || this.$store.getters.getToolbarLoad"
      >
        <v-icon>mdi-download</v-icon>
      </v-btn>
      <v-btn
        color="red"
        icon
        class="mx-2 white--text"
        @click="deleteSelected"
        :disabled="!selected.length || this.$store.getters.getToolbarLoad"
      >
        <v-icon>mdi-delete</v-icon>
      </v-btn>
    </v-row>

    <!-- Old Videos Table -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="videos"
          item-value="id"
          class="elevation-1"
          show-select
          :footer-props="{ itemsPerPageOptions: [10, 20, 50] }"
          :server-items-length="total"
          :options.sync="options"
          @update:options="fetchVideos"
        >
          <template v-slot:item.name="{ item }">
            <v-row
              align="center"
              class="py-5"
              @click="openPreview(item)"
              style="cursor: pointer"
            >
              <div class="thumbnail">
                <v-img
                  :src="item.thumb_url || item.yt_thumb_url"
                  :height="50"
                  :aspect-ratio="16 / 9"
                  contain
                  class="mr-2"
                ></v-img>
              </div>
              <span class="truncate">{{ item.title }}</span>
            </v-row>
          </template>
          <template v-slot:item.views="{ item }">
            <span>
              {{ item.views.toLocaleString("en-GB") }}
            </span>
          </template>
          <template v-slot:item.channel="{ item }">
            <div>
              {{ item.channel.name }}
            </div>
            <div>
              Follows: {{ item.channel.followers.toLocaleString("en-GB") }}
            </div>
            <div>Views: {{ item.channel.views.toLocaleString("en-GB") }}</div>
          </template>
          <template v-slot:item.date="{ item }">
            <span>{{ formatDate(item[filters.date_field]) }}</span>
          </template>
          <template v-slot:item.downloaded="{ item }">
            <v-checkbox
              v-model="item.downloaded"
              @change="markAsDownloaded(item)"
            />
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-dialog v-model="previewDialog" max-width="800px" @click:outside="closePreview">
      <v-card>
        <v-card-title class="headline">{{ previewVideo.title }}</v-card-title>
        <v-card-text>
          <video
            v-if="previewVideo.video_url"
            :src="previewVideo.video_url"
            controls
            autoplay
            style="width: 100%; height: auto"
          ></video>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red" text @click="closePreview">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog :value="true" v-if="showDialog" max-width="290">
      <v-card>
        <v-card-title class="text-h5">
          {{ $t("delete") }}
        </v-card-title>
        <v-card-text>
          {{ $t("are you sure") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />

          <v-btn color="green darken-1" text @click="this.onCancelDelete">
            {{ $t("cancel") }}
          </v-btn>

          <v-btn color="green darken-1" text @click="this.onConfirmDelete">
            {{ $t("delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import JSZip from "jszip";
import api from "@/api/api";

export default {
  data() {
    const today = new Date();
    const threeYearsAgo = new Date(today.setFullYear(today.getFullYear() - 3));

    return {
      showDialog: false,
      previewDialog: false,
      previewVideo: {},
      videos: [],
      total: 0,
      selected: [],
      headers: [
        { text: "Video Name", value: "name", sortable: false },
        { text: "Views", value: "views", sortable: false },
        { text: "Channel", value: "channel", sortable: false },
        { text: "Platform", value: "platform", sortable: false },
        { text: "Date", value: "date", sortable: true },
        { text: "Downloaded", value: "downloaded", sortable: false },
      ],
      filters: {
        date_field: "created_at",
        date: threeYearsAgo.toISOString().substr(0, 10),
      },
      sortDirections: ["asc", "desc"],
      dateFields: ["created_at", "publish_time"],
      datePickerMenu: false,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
      },
    };
  },
  methods: {
    fetchVideos() {
      this.$store.dispatch("SET_TOOLBARLOAD", true);
      const params = {
        sort_dir: this.options.sortDesc[0] ? "desc" : "asc",
        date_field: this.filters.date_field,
        date: this.filters.date,
        offset: (this.options.page - 1) * this.options.itemsPerPage,
        limit: this.options.itemsPerPage,
      };

      api
        .getOldVideos(params)
        .then((res) => {
          this.videos = res.data.videos;
          this.total = res.data.total;
          this.$store.dispatch("SET_TOOLBARLOAD", false);
        })
        .catch((err) => {
          console.error(err);
          this.$store.dispatch("SET_TOOLBARLOAD", false);
        });
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },
    deleteSelected() {
      this.showDialog = true;
    },
    onCancelDelete() {
      this.showDialog = false;
    },
    onConfirmDelete() {
      this.showDialog = false;
      const ids = this.selected.map((item) => item.id);
      this.$store.dispatch("SET_TOOLBARLOAD", true);
      api
        .deleteVideos(ids)
        .then(() => {
          this.fetchVideos();
          this.selected = [];
          this.$store.dispatch("SET_TOOLBARLOAD", false);
        })
        .catch((err) => {
          console.error(err);
          this.$store.dispatch("SET_TOOLBARLOAD", false);
        });
    },
    async downloadSelected() {
      const zip = new JSZip();
      const folder = zip.folder("videos");
      try {
        this.$store.dispatch("SET_TOOLBARLOAD", true);
        for (const item of this.selected) {
          const response = await fetch(item.video_url);
          const blob = await response.blob();
          const fileName = item.video_url.split("/").pop();
          folder.file(`video-${item.id}--${fileName}`, blob);
        }

        zip.generateAsync({ type: "blob" }).then((content) => {
          const link = document.createElement("a");
          link.href = URL.createObjectURL(content);
          link.download = `videos_${Date.now()}.zip`;
          link.click();
          this.$store.dispatch("SET_TOOLBARLOAD", false);
        });
      } catch (error) {
        console.error("Error downloading videos:", error);
        this.$store.dispatch("SET_TOOLBARLOAD", false);
      }
    },
    markAsDownloaded(item) {
      const status = item.downloaded;
      api
        .editVideo(item.id, { downloaded: status })
        .then(() => {
          console.log("Marked as downloaded");
        })
        .catch((err) => {
          console.error(err);
        });
    },
    openPreview(item) {
      this.previewVideo = item;
      this.previewDialog = true;
    },
    closePreview() {
      this.previewDialog = false;
      this.previewVideo = {};
    },
  },
  watch: {
    filters: {
      handler: "fetchVideos",
      deep: true,
    },
  },
  mounted() {
    this.fetchVideos();
  },
};
</script>

<style scoped>
.thumbnail {
  margin-right: 5px;
  height: 50px;
  aspect-ratio: 16 / 9;
  background: black;
  object-fit: cover;
  background-size: cover;
  background-position: center center;
}
.truncate {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 400px !important;
  white-space: normal;
}
</style>
